import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { UserFunctionsService } from '@functions/user.service';
import { BusinessUser, BusinessUserStatus } from '@models/businesses';
import { SnackbarService } from '@services/ui/snackbar.service';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import {
	MatterService,
	TransferActiveMatterOwnershipModel,
} from 'src/app/shared/api-repos/matter/matter.service';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { ContentFooterAction } from 'src/app/ui/content-footer/content-footer.component';

export interface DeactivateUserModalData {
	currentUser: BusinessUser;
	allUsers: BusinessUser[];
	assignedMatterCount: number;
}

interface UsersSelection {
	value: BusinessUser;
	name: string;
}

interface VM {
	content: string;
	disclaimer?: string;
	title?: string;
	primaryAction?: ContentFooterAction;
	leftAction?: ContentFooterAction;
	usersSelectionItems: UsersSelection[];
	selectedUser: UsersSelection;
	currentUser: BusinessUser;
	showDropdown?: boolean;
}

const defaultVM: VM = {
	title: 'Deactivate User?',
	content: '',
	disclaimer: '',
	primaryAction: {
		text: 'Confirm',
		action: () => {},
		buttonPreset: BUTTON_PRESETS.HIGH_EMPHASIS,
		disabled: false,
		loading: false,
	},
	leftAction: {
		text: 'Cancel',
		action: () => {},
		buttonPreset: BUTTON_PRESETS.EXTRA_LOW_EMPHASIS,
		disabled: false,
	},
	selectedUser: null,
	usersSelectionItems: [],
	currentUser: null,
	showDropdown: false,
};

@Component({
	selector: 'app-deactivate-user-modal',
	templateUrl: './deactivate-user-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DeactivateUserModalComponent extends BaseComponent<VM> {
	public allUsers: BusinessUser[];

	public usersSelectionItems: UsersSelection[] = [];

	constructor(
		private dialogRef: MatDialogRef<DeactivateUserModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DeactivateUserModalData,
		private matterService: MatterService,
		private snackBarService: SnackbarService,
		private userFunctionService: UserFunctionsService,
	) {
		super(defaultVM);

		this.allUsers = this.data.allUsers;

		this.usersSelectionItems = this.allUsers
			.filter(
				(user) =>
					user.id !== this.data.currentUser.id &&
					user.status === BusinessUserStatus.ACTIVE &&
					user.role === this.data.currentUser.role,
			)
			.sort((a, b) => a.email?.localeCompare(b.email))
			.map((user) => {
				return {
					value: user,
					name: user?.firstName + ' ' + user?.lastName,
				};
			});

		this.usersSelectionItems.unshift({
			value: null,
			name: 'Do Not Reassign',
		});

		const userName = `${this.data.currentUser?.firstName} ${this.data.currentUser?.lastName}`;
		if (this.data.assignedMatterCount > 0) {
			this.VM = {
				primaryAction: {
					...this.VM.primaryAction,
					action: () => this.transferMattersAndDeactivate(),
				},
				leftAction: {
					...this.VM.leftAction,
					action: () => this.closeModal(),
				},
				usersSelectionItems: this.usersSelectionItems,
				disclaimer: `Would you like to reassign ${userName}'s active matters to a colleague? If not, ${userName} will be removed from all active matters, and won't be replaced.`,
				content: `Active Matters Assigned to ${userName} (${this.data.assignedMatterCount})`,
				title: 'Reassign Active Matters?',
				currentUser: this.data.currentUser,
				showDropdown: true,
			};
		} else {
			this.VM = {
				primaryAction: {
					...this.VM.primaryAction,
					action: () => this.deactivateUser(),
				},
				leftAction: {
					...this.VM.leftAction,
					action: () => this.closeModal(),
				},
				usersSelectionItems: this.usersSelectionItems,
				content: `Are you sure you want to deactivate ${this.data.currentUser?.firstName} ${this.data.currentUser?.lastName}'s account?`,
				currentUser: this.data.currentUser,
			};
		}
	}

	transferMattersAndDeactivate() {
		this.VM = {
			primaryAction: {
				...this.VM.primaryAction,
				loading: true,
				disabled: true,
			},
		};

		// If a user is selected, transfer the matters to that user otherwise just deactivate the user
		if (this.VM.selectedUser) {
			const transferModel = {
				deactivatedUser: {
					id: this.VM.currentUser.id,
					initials: this.VM.currentUser?.initials ?? '',
					name:
						this.VM.currentUser?.firstName +
						' ' +
						this.VM.currentUser?.lastName,
					phoneNumber: this.VM.currentUser?.phoneNumber ?? '',
				},
				transferToUser: {
					id: this.VM.selectedUser.value.id,
					initials: this.VM.selectedUser.value?.initials ?? '',
					name:
						this.VM.selectedUser.value?.firstName +
						' ' +
						this.VM.selectedUser.value?.lastName,
					phoneNumber: this.VM.selectedUser.value?.phoneNumber ?? '',
				},
				role: this.VM.currentUser?.role,
			} as TransferActiveMatterOwnershipModel;

			this.matterService.TransferActiveMatterOwnership(transferModel).subscribe(
				() => {
					this.remove(this.VM.currentUser, true);
				},
				(error) => {
					console.error(error);
					this.snackBarService.openSnackBar({
						text: 'Could not transfer matters. Please try again.',
						type: 'error',
					});
					this.closeModal();
				},
			);
		} else {
			this.remove(this.VM.currentUser);
		}
	}

	async remove(
		user: BusinessUser,
		transferredMatters: boolean = false,
	): Promise<void> {
		try {
			await this.userFunctionService
				.makeUserInactive(user.id)
				.pipe(take(1))
				.toPromise();
			this.snackBarService.openSnackBar({
				text: `${user.firstName} ${user.lastName} successfully${transferredMatters ? ' transferred matters and' : ''} deactivated`,
				type: 'success',
			});
		} catch (err) {
			LogRocket.captureException(err);
			LogRocket.log('Unable to delete user', user.id, err);
			this.snackBarService.openSnackBar({
				text: `There was an error when attempting to deactivate user ${user.firstName} ${user.lastName}`,
				type: 'error',
			});
		}
		this.closeModal();
	}

	deactivateUser() {
		this.remove(this.VM.currentUser);
	}

	userSelected($event: DropdownResult<UsersSelection>) {
		if ($event.result.value) {
			this.VM = {
				primaryAction: {
					...this.VM.primaryAction,
					disabled: false,
				},
				selectedUser: $event.result,
			};
		} else {
			this.VM = {
				selectedUser: null,
			};
		}
	}

	closeModal() {
		this.dialogRef.close();
	}
}
